import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useDashboardTracking } from '../../hooks/useDashboardTracking';
import DateTimePickerInput from '../../components/DateTimePickerInput';

// Chart components
import AgingDistributionChart from './components/charts/AgingDistributionChart';
import ServiceRequestStateBarChart from './components/charts/ServiceRequestStateBarChart';
import ServiceRequestPriorityBarChart from './components/charts/ServiceRequestPriorityBarChart';
import GroupWiseChangesChart from './components/charts/GroupWiseChangesChart';
import CustomerWiseChangesChart from './components/charts/CustomerWiseChangesChart';
import SLAComplianceGauge from './components/charts/SLAComplianceGauge';
import UnifiedPulseReportTable from './components/UnifiedPulseReportTable';

// Utils
import { DurationPreset, durationPresets, convertPresetToTimeRange, getDurationPresetRange } from './utils/dateHelpers';

interface Customer {
  id: string;
  accountName: string;
}

interface Group {
  id: string;
  name: string;
}

interface ChartFilter {
  state?: string;
  priority?: string;
  filterType?: string;
  filterValue?: string;
  agingBucket?: string;
  slaBreached?: boolean;
}

const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'awaiting_info', label: 'Awaiting Info' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const IncidentDashboard: React.FC = () => {
  // Subscribe to real-time incident updates via SSE
  useTicketEvents({ ticketTypes: ['INCIDENT'] });

  // Get date formatter hook
  const { formatTime } = useDateFormatter();

  // Filter state
  const [customerId, setCustomerId] = useState<string>('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('this_month');
  const [previousPreset, setPreviousPreset] = useState<DurationPreset>('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [selectedStates, setSelectedStates] = useState<string[]>([]);

  // Chart interaction state
  const [tableVisible, setTableVisible] = useState(false);
  const [tableFilterLabel, setTableFilterLabel] = useState('');
  const [chartFilters, setChartFilters] = useState<ChartFilter>({});

  // Handle duration preset change
  const handleDurationChange = (newPreset: DurationPreset) => {
    if (newPreset === 'custom') {
      // Pre-populate custom dates based on the previous preset's range
      const range = getDurationPresetRange(previousPreset);
      setCustomDateFrom(range.from);
      setCustomDateTo(range.to);
      // Don't set applied dates yet - wait for Apply button
    } else {
      // For non-custom presets, update the previous preset tracker
      setPreviousPreset(newPreset);
      // Clear custom date state
      setCustomDateFrom('');
      setCustomDateTo('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
    }
    setDurationPreset(newPreset);
  };

  // Compute time range for API - uses applied dates for custom range
  const timeRange = useMemo(() => {
    if (durationPreset === 'custom') {
      // Only return custom range if dates have been applied
      if (appliedDateFrom && appliedDateTo) {
        return convertPresetToTimeRange('custom', appliedDateFrom, appliedDateTo);
      }
      // Keep using the previous preset's range until Apply is clicked
      return convertPresetToTimeRange(previousPreset);
    }
    return convertPresetToTimeRange(durationPreset);
  }, [durationPreset, appliedDateFrom, appliedDateTo, previousPreset]);

  // Handle applying custom date range
  const handleApplyCustomDates = () => {
    if (customDateFrom && customDateTo) {
      setAppliedDateFrom(customDateFrom);
      setAppliedDateTo(customDateTo);
    }
  };

  // Check if custom dates have been modified and need to be applied
  const customDatesNeedApply = durationPreset === 'custom' &&
    customDateFrom && customDateTo &&
    (customDateFrom !== appliedDateFrom || customDateTo !== appliedDateTo);

  // Track dashboard filters for Application Insights
  useDashboardTracking('Incident', {
    customerId,
    durationPreset,
    assignmentGroupId,
    selectedStates,
    customDateFrom: appliedDateFrom || undefined,
    customDateTo: appliedDateTo || undefined,
  });

  // Get timezone offset
  const timezoneOffset = new Date().getTimezoneOffset();

  // States query param
  const statesParam = selectedStates.join(',');

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || response.data || [];
    },
  });

  // Fetch all assignment groups for filtering
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['all-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment&limit=500');
      return response.data.groups || response.data || [];
    },
  });

  // Fetch dashboard data with fresh data on every mount/tab focus
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['incident-dashboard', customerId, timeRange, assignmentGroupId, timezoneOffset, statesParam],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupId) params.append('assignmentGroupId', assignmentGroupId);
      if (statesParam) params.append('states', statesParam);
      params.append('timezoneOffset', String(timezoneOffset));

      const response = await api.get(`/dashboard/incident?${params}`);
      return response.data;
    },
    staleTime: 0,               // Always fetch fresh data on mount
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always',   // Always refetch on component mount
  });

  // Chart click handlers
  const handleTotalClick = () => {
    setTableFilterLabel('All Incidents');
    setChartFilters({});
    setTableVisible(true);
  };

  const handleCriticalUrgentClick = () => {
    setTableFilterLabel('Critical/Urgent Incidents');
    setChartFilters({ filterType: 'criticalUrgent' });
    setTableVisible(true);
  };

  const handleUnassignedClick = () => {
    setTableFilterLabel('Unassigned Incidents');
    setChartFilters({ filterType: 'unassigned' });
    setTableVisible(true);
  };

  const handleOpenIncidentsClick = () => {
    setTableFilterLabel('Open Incidents');
    setChartFilters({ filterType: 'open', filterValue: 'all' });
    setTableVisible(true);
  };

  const handleClosedIncidentsClick = () => {
    setTableFilterLabel('Closed Incidents');
    setChartFilters({ state: 'closed' });
    setTableVisible(true);
  };

  const handleAgingClick = (bucket: string) => {
    const labels: Record<string, string> = {
      '2-5': '2-5 Days Old',
      '6-10': '6-10 Days Old',
      '11-15': '11-15 Days Old',
      '16-30': '16-30 Days Old',
      '>30': 'Over 30 Days Old',
    };
    setTableFilterLabel(`${labels[bucket]} Incidents`);
    setChartFilters({ agingBucket: bucket });
    setTableVisible(true);
  };

  const handleStateClick = (state: string) => {
    const stateLabels: Record<string, string> = {
      new: 'New',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      awaiting_info: 'Awaiting Info',
      resolved: 'Resolved',
      closed: 'Closed',
      cancelled: 'Cancelled',
    };
    setTableFilterLabel(`${stateLabels[state] || state} Incidents`);
    setChartFilters({ state });
    setTableVisible(true);
  };

  const handlePriorityClick = (priority: string) => {
    const priorityLabels: Record<string, string> = {
      critical_1: 'Critical',
      high_2: 'High',
      moderate_3: 'Moderate',
      low_4: 'Low',
      planning_5: 'Planning',
    };
    setTableFilterLabel(`${priorityLabels[priority] || priority} Priority Incidents`);
    setChartFilters({ priority });
    setTableVisible(true);
  };

  const handleGroupClick = (groupId: string, groupName: string) => {
    setTableFilterLabel(`Incidents - ${groupName}`);
    setChartFilters({ filterType: 'assignmentGroup', filterValue: groupId });
    setTableVisible(true);
  };

  const handleCustomerClick = (clickedCustomerId: string, customerName: string) => {
    setTableFilterLabel(`Incidents - ${customerName}`);
    setChartFilters({ filterType: 'customer', filterValue: clickedCustomerId });
    setTableVisible(true);
  };

  const handleSlaClick = (slaBreached: boolean) => {
    setTableFilterLabel(slaBreached ? 'SLA Breached Incidents' : 'SLA Met Incidents');
    setChartFilters({ slaBreached });
    setTableVisible(true);
  };

  const closeTable = () => {
    setTableVisible(false);
    setChartFilters({});
  };

  // Handle state checkbox changes
  const handleStateChange = (value: string) => {
    setSelectedStates(prev =>
      prev.includes(value)
        ? prev.filter(s => s !== value)
        : [...prev, value]
    );
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!dashboardData) return;
    console.log('Export PDF functionality to be implemented');
  };

  const selectedCustomerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName}
                </option>
              ))}
            </select>
          </div>

          {/* Duration Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={durationPreset}
              onChange={(e) => handleDurationChange(e.target.value as DurationPreset)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {durationPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group</label>
            <select
              value={assignmentGroupId}
              onChange={(e) => setAssignmentGroupId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export PDF Button and Refresh */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!dashboardData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Last Updated Indicator */}
        {dataUpdatedAt && (
          <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
            {isFetching && (
              <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            )}
            <span>
              Last updated: {formatTime(new Date(dataUpdatedAt))}
            </span>
          </div>
        )}

        {/* Custom Date Range (shown when custom is selected) */}
        {durationPreset === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <DateTimePickerInput
                value={customDateFrom}
                onChange={(val) => setCustomDateFrom(val)}
                showTimeSelect={true}
                className="w-full"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DateTimePickerInput
                  value={customDateTo}
                  onChange={(val) => setCustomDateTo(val)}
                  showTimeSelect={true}
                  className="w-full"
                />
              </div>
              <button
                onClick={handleApplyCustomDates}
                disabled={!customDateFrom || !customDateTo}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  customDatesNeedApply
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* State Filter Checkboxes */}
        <div className="mt-4 border-t border-gray-200 pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by State</label>
          <div className="flex flex-wrap gap-3">
            {stateOptions.map((option) => (
              <label
                key={option.value}
                className="inline-flex items-center cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedStates.includes(option.value)}
                  onChange={() => handleStateChange(option.value)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
            {selectedStates.length > 0 && (
              <button
                onClick={() => setSelectedStates([])}
                className="text-sm text-primary-600 hover:text-primary-800 ml-2"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load dashboard data. Please try again.
          <button onClick={() => refetch()} className="ml-4 text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !isLoading && (
        <>
          {/* Summary Tiles - Order: Total, Open, Critical, Closed, Unassigned, SLA Compliance */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all"
              onClick={handleTotalClick}
            >
              <div className="text-2xl font-bold text-gray-900">{dashboardData.summary?.total || 0}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all"
              onClick={handleOpenIncidentsClick}
            >
              <div className="text-2xl font-bold text-blue-600">{dashboardData.summary?.open || 0}</div>
              <div className="text-sm text-gray-500">Open</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-red-50 transition-all"
              onClick={handleCriticalUrgentClick}
            >
              <div className="text-2xl font-bold text-red-600">{dashboardData.summary?.criticalUrgent || 0}</div>
              <div className="text-sm text-gray-500">Critical</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-green-50 transition-all"
              onClick={handleClosedIncidentsClick}
            >
              <div className="text-2xl font-bold text-green-600">{dashboardData.summary?.closed || 0}</div>
              <div className="text-sm text-gray-500">Closed</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-orange-50 transition-all"
              onClick={handleUnassignedClick}
            >
              <div className="text-2xl font-bold text-orange-600">{dashboardData.summary?.unassigned || 0}</div>
              <div className="text-sm text-gray-500">Unassigned</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-purple-600">{dashboardData.summary?.slaCompliance || 0}%</div>
              <div className="text-sm text-gray-500">SLA Compliance</div>
            </div>
          </div>

          {/* Row 1: Aging Distribution and Priority */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AgingDistributionChart
              data={dashboardData.agingDistribution || { '2-5': 0, '6-10': 0, '11-15': 0, '16-30': 0, '>30': 0 }}
              onBarClick={handleAgingClick}
              title="Incident Aging Distribution"
            />
            <ServiceRequestPriorityBarChart
              data={dashboardData.byPriority || {
                critical_1: 0, high_2: 0, moderate_3: 0, low_4: 0, planning_5: 0
              }}
              onBarClick={handlePriorityClick}
            />
          </div>

          {/* Row 2: State Distribution and Assignment Group */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ServiceRequestStateBarChart
              data={dashboardData.byState || {
                new: 0, in_progress: 0, on_hold: 0, awaiting_info: 0,
                resolved: 0, closed: 0, cancelled: 0
              }}
              onBarClick={handleStateClick}
            />
            <GroupWiseChangesChart
              data={dashboardData.byAssignmentGroup || []}
              title="Assignment Group-wise Incidents"
              chartId="chart-inc-group-wise"
              onBarClick={handleGroupClick}
            />
          </div>

          {/* Row 3: Customer Distribution and SLA Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CustomerWiseChangesChart
              data={dashboardData.byCustomer || []}
              onBarClick={handleCustomerClick}
              title="Customer-wise Incidents"
            />
            <SLAComplianceGauge
              slaCompliance={dashboardData.summary?.slaCompliance || 0}
              slaMet={dashboardData.summary?.slaMet || 0}
              slaBreached={dashboardData.summary?.slaBreached || 0}
              totalSlaApplied={(dashboardData.summary?.slaMet || 0) + (dashboardData.summary?.slaBreached || 0)}
              onSlaClick={handleSlaClick}
            />
          </div>

          {/* Filtered Tickets Table */}
          <UnifiedPulseReportTable
            visible={tableVisible}
            filterLabel={tableFilterLabel}
            baseFilters={{
              customerId,
              timeRange,
              assignmentGroupId,
              timezoneOffset,
            }}
            chartFilters={{
              ticketType: 'INCIDENT',
              state: chartFilters.state,
              priority: chartFilters.priority,
              slaBreached: chartFilters.slaBreached !== undefined ? String(chartFilters.slaBreached) : undefined,
              filterType: chartFilters.filterType,
              filterValue: chartFilters.filterValue,
              agingBucket: chartFilters.agingBucket,
            }}
            onClose={closeTable}
            ticketType="INCIDENT"
            customerName={selectedCustomerName}
          />
        </>
      )}
    </div>
  );
};

export default IncidentDashboard;
